import React from "react";

const operators = ["MNO 1", "MNO 2", "MNO 3", "MNO 4", "MNO 5"];
const verticals = [
  "Cities",
  "Statistical offices",
  "Transport",
  "Tourism",
  "Retail",
  "Emergency",
];

/* Layout constants — operators and verticals fit cleanly above the caption row.
   ViewBox is 900×400 with the caption row reserved at y=370–395. */
const VB_W = 900;
const VB_H = 400;
const CAPTION_Y = 388;

const op_top = 40;
const op_step = 50;
const op_h = 36;
const op_x = 40;
const op_w = 140;

const vert_top = 50;
const vert_step = 42;
const vert_h = 32;
const vert_x = 720;
const vert_w = 160;

const pipe_x = 300;
const pipe_w = 300;
const pipe_y = 145;
const pipe_h = 90;

/* Three internal segments along the pipeline. The first (Anonymise) is shaded
   slightly to signal "still inside the operator boundary"; Enrich and Serve
   are on Telcofy's side. */
const seg_w = pipe_w / 3;
const seg_titles = ["Anonymise", "Enrich", "Serve"];
const seg_subtitles = ["inside operator", "context layered", "API · dashboards"];
const seg_center_x = (i) => pipe_x + i * seg_w + seg_w / 2;

const BridgeDiagram = () => {
  const opCenterY = (i) => op_top + i * op_step + op_h / 2;
  const vertCenterY = (i) => vert_top + i * vert_step + vert_h / 2;

  // Connections target the centre of the first segment (Anonymise) on the
  // left and exit from the centre of the last segment (Serve) on the right.
  const inX = pipe_x;          // left edge of pipeline
  const outX = pipe_x + pipe_w; // right edge of pipeline

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      className="w-full h-auto"
      role="img"
      aria-label="Telcofy bridge: many mobile network operators feed one neutral pipeline (Anonymise → Enrich → Serve) that serves many verticals"
    >
      {/* Pipeline outer shell */}
      <rect
        x={pipe_x}
        y={pipe_y}
        width={pipe_w}
        height={pipe_h}
        rx="44"
        fill="#FFFFFF"
        stroke="#4CAF50"
        strokeWidth="2"
      />

      {/* Anonymise segment shading — signals "still inside operator boundary" */}
      <clipPath id="pipeShape">
        <rect
          x={pipe_x}
          y={pipe_y}
          width={pipe_w}
          height={pipe_h}
          rx="44"
        />
      </clipPath>
      <g clipPath="url(#pipeShape)">
        <rect
          x={pipe_x}
          y={pipe_y}
          width={seg_w}
          height={pipe_h}
          fill="#F2F2F2"
          opacity="0.6"
        />
      </g>
      <rect
        x={pipe_x}
        y={pipe_y}
        width={pipe_w}
        height={pipe_h}
        rx="44"
        fill="none"
        stroke="#4CAF50"
        strokeWidth="2"
      />

      {/* Segment dividers */}
      {[1, 2].map((i) => (
        <line
          key={`div-${i}`}
          x1={pipe_x + i * seg_w}
          y1={pipe_y + 14}
          x2={pipe_x + i * seg_w}
          y2={pipe_y + pipe_h - 14}
          stroke="#E5E6E6"
          strokeWidth="1"
        />
      ))}

      {/* Segment labels */}
      {seg_titles.map((title, i) => (
        <g key={`seg-${i}`}>
          <text
            x={seg_center_x(i)}
            y={pipe_y + 28}
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="9"
            fill="#9CA3AF"
            letterSpacing="1.5"
          >
            {String(i + 1).padStart(2, "0")}
          </text>
          <text
            x={seg_center_x(i)}
            y={pipe_y + 52}
            textAnchor="middle"
            fontFamily="Poppins, Inter, sans-serif"
            fontWeight="700"
            fontSize="15"
            fill="#1E1E1E"
          >
            {title}
          </text>
          <text
            x={seg_center_x(i)}
            y={pipe_y + 70}
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            fontSize="10"
            fill="#6B7280"
          >
            {seg_subtitles[i]}
          </text>
        </g>
      ))}

      {/* Upstream connections — fan into the Anonymise segment (segment 0) */}
      {operators.map((label, i) => {
        const cy = opCenterY(i);
        const targetY = pipe_y + pipe_h / 2;
        return (
          <g key={`up-${i}`}>
            <rect
              x={op_x}
              y={op_top + i * op_step}
              width={op_w}
              height={op_h}
              rx="18"
              fill="#F2F2F2"
              stroke="#E5E6E6"
            />
            <circle cx={op_x + 22} cy={cy} r="5" fill="#4CAF50" opacity="0.4" />
            <text
              x={op_x + 40}
              y={cy + 4}
              fontFamily="Inter, sans-serif"
              fontSize="12"
              fill="#4B5563"
              fontWeight="500"
            >
              {label}
            </text>
            <path
              d={`M ${op_x + op_w} ${cy} Q ${(op_x + op_w + inX) / 2} ${cy}, ${inX} ${targetY}`}
              fill="none"
              stroke="#4CAF50"
              strokeWidth="1.2"
              opacity="0.5"
            />
          </g>
        );
      })}

      {/* Downstream connections — fan out of the Serve segment (segment 2) */}
      {verticals.map((label, i) => {
        const cy = vertCenterY(i);
        const sourceY = pipe_y + pipe_h / 2;
        return (
          <g key={`down-${i}`}>
            <path
              d={`M ${outX} ${sourceY} Q ${(outX + vert_x) / 2} ${cy}, ${vert_x} ${cy}`}
              fill="none"
              stroke="#4CAF50"
              strokeWidth="1.2"
              opacity="0.5"
            />
            <rect
              x={vert_x}
              y={vert_top + i * vert_step}
              width={vert_w}
              height={vert_h}
              rx="16"
              fill="#FFFFFF"
              stroke="#4CAF50"
              strokeWidth="1"
            />
            <text
              x={vert_x + 18}
              y={cy + 4}
              fontFamily="Inter, sans-serif"
              fontSize="12"
              fill="#1E1E1E"
              fontWeight="500"
            >
              {label}
            </text>
          </g>
        );
      })}

      {/* Caption row — sits in its own reserved band at the bottom. */}
      <text
        x={op_x + op_w / 2}
        y={CAPTION_Y}
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize="10"
        fill="#6B7280"
        letterSpacing="1.5"
      >
        MANY OPERATORS IN
      </text>
      <text
        x={pipe_x + pipe_w / 2}
        y={CAPTION_Y}
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize="10"
        fill="#6B7280"
        letterSpacing="1.5"
      >
        ONE PIPELINE
      </text>
      <text
        x={vert_x + vert_w / 2}
        y={CAPTION_Y}
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize="10"
        fill="#6B7280"
        letterSpacing="1.5"
      >
        MANY VERTICALS OUT
      </text>
    </svg>
  );
};

const BridgeSection = () => {
  return (
    <section id="bridge" className="py-20 bg-paper-200 border-y border-paper-300">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mb-10">
          <p className="text-xs font-semibold tracking-widest text-brand-600 uppercase mb-3">
            The bridge
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink tracking-tight mb-3">
            One pipeline. Many operators in. Many verticals out.
          </h2>
          <p className="text-base md:text-lg text-ink-muted leading-relaxed">
            Telcofy is the neutral layer that sits between the operators that
            generate mobility signals and the cities, statistical offices, and
            commercial teams that need to act on them.
          </p>
        </div>

        <div className="bg-paper border border-paper-300 rounded-lg p-4 md:p-8 shadow-brand-sm overflow-x-auto">
          <BridgeDiagram />
        </div>
      </div>
    </section>
  );
};

export default BridgeSection;
